import { lazy, Suspense } from 'react'
import {
  createBrowserRouter,
  RouterProvider,
} from 'react-router-dom'
import { PublicLayout } from './layout/public-layout'
import HomePage from '@/features/home/home-page'
import NotFoundPage from './routes/not-found'
import { RouteFallback } from './routes/route-fallback'
import { RouteErrorBoundary } from '@/shared/ui/route-error-boundary'
import { ProtectedRoute } from '@/features/auth/components/protected-route'
import { PageStatusGate } from '@/features/page-status/page-status-gate'

/**
 * App router. Code-splitting per route, suspense fallback for the loading
 * state, and a per-route `RouteErrorBoundary` so a render bug on one
 * surface doesn't take down the navbar or other routes.
 *
 * `withFallback(Component, routeId)`: every route owns a stable `routeId`
 * the boundary uses for tagging error reports — useful when scanning
 * Sentry to spot a single page failing repeatedly.
 */
const ToolDetailPage = lazy(() => import('@/features/tools-hub/tool-detail-page'))
const ToolsHubShellPage = lazy(() => import('@/features/tools-hub/tools-hub-shell-page'))
const FreeToolsPage = lazy(() => import('@/features/free-tools/free-tools-page'))
const FreeToolDetailPage = lazy(() => import('@/features/free-tools/free-tool-detail-page'))
const ProToolsPage = lazy(() => import('@/features/pro-tools/pro-tools-page'))
const AiSearchPage = lazy(() => import('@/features/ai-search/ai-search-page'))
const ZatoonaHubPage = lazy(() => import('@/features/zatoona/zatoona-hub-page'))
const ZatoonaCategoryPage = lazy(() => import('@/features/zatoona/zatoona-category-page'))
const ZatoonaReferencePage = lazy(() => import('@/features/zatoona/zatoona-reference-page'))
const PromptGuidePage = lazy(() => import('@/features/prompt-guide/prompt-guide-page'))
const FreeApisHubPage = lazy(() => import('@/features/free-apis/free-apis-hub-page'))
const FreeApiDetailPage = lazy(() => import('@/features/free-apis/free-api-detail-page'))
const PromptDetailPage = lazy(() => import('@/features/prompts/prompt-detail-page'))
const PromptsShellPage = lazy(() => import('@/features/prompts/prompts-shell-page'))
const TutorialsPage = lazy(() => import('@/features/tutorials/tutorials-page'))
const TutorialsPlaylistPage = lazy(() => import('@/features/tutorials/playlist-page'))
const BlogPage = lazy(() => import('@/features/blog/blog-page'))
const ArticlePage = lazy(() => import('@/features/blog/article-page'))
const CoursesPage = lazy(() => import('@/features/courses/courses-page'))
const CourseDetailPage = lazy(() => import('@/features/courses/course-detail-page'))
const CourseLearnPage = lazy(() => import('@/features/courses/course-learn-page'))
const CheckoutPage = lazy(() => import('@/features/checkout/checkout-page'))
const PaymentSuccessPage = lazy(() => import('@/features/checkout/payment-success-page'))
const LoginPage = lazy(() => import('@/features/auth/login-page'))
const RegisterPage = lazy(() => import('@/features/auth/register-page'))
const ForgotPasswordPage = lazy(() => import('@/features/auth/forgot-password-page'))
const StudentDashboardPage = lazy(() => import('@/features/student/dashboard-page'))
const StudentSettingsPage = lazy(() => import('@/features/student/settings-page'))
const StudentCertificatesPage = lazy(() => import('@/features/student/certificates-page'))
const VerifyCertificatePage = lazy(() => import('@/features/student/verify-certificate-page'))
const LibraryPage = lazy(() => import('@/features/saved-items/library-page'))
const PublicLibraryPage = lazy(() => import('@/features/saved-items/public-library-page'))
const ContactPage = lazy(() => import('@/features/contact/contact-page'))
const AboutPage = lazy(() => import('@/features/about/about-page'))
const PrivacyPage = lazy(() => import('@/features/legal/privacy-page'))
const TermsPage = lazy(() => import('@/features/legal/terms-page'))
const ComingSoonPage = lazy(() => import('@/app/routes/coming-soon'))
const MaintenancePage = lazy(() => import('@/app/routes/maintenance'))
const VibeCodingPage = lazy(() => import('@/features/vibe-coding/vibe-coding-page'))
const VibeDetailPage = lazy(() => import('@/features/vibe-coding/vibe-detail-page'))
const DesignSystemsPage = lazy(() => import('@/features/design-systems/design-systems-page'))
const DesignSystemDetailPage = lazy(() => import('@/features/design-systems/design-system-detail-page'))
const HowToUseDesignMdPage = lazy(() => import('@/features/design-systems/how-to-use-page'))
const AiCourseLandingPage = lazy(() => import('@/features/ai-course/ai-course-page'))
const AiToolsPillarPage = lazy(() => import('@/features/pillars/ai-tools-page'))
const FreeAiToolsPillarPage = lazy(() => import('@/features/pillars/free-ai-tools-page'))
const AiPromptsPillarPage = lazy(() => import('@/features/pillars/ai-prompts-page'))
const AiDesignCoursePillarPage = lazy(() => import('@/features/pillars/ai-design-course-page'))
const AiVideoCoursePillarPage = lazy(() => import('@/features/pillars/ai-video-course-page'))
const PeHubPage = lazy(() => import('@/features/prompt-engineering/pe-hub-page'))
const PePage = lazy(() => import('@/features/prompt-engineering/pe-page'))
const GlossaryPage = lazy(() => import('@/features/glossary/glossary-page'))
const AiNewsPage = lazy(() => import('@/features/ai-news/ai-news-page'))
const AiNewsDetailPage = lazy(() => import('@/features/ai-news/ai-news-detail-page'))
const AssistantPage = lazy(() => import('@/features/assistant/assistant-page'))

// Admin (lazy chunk — only loaded when an admin user navigates to /iamnotsite)
const AdminShell = lazy(() =>
  import('@/features/admin/components/admin-shell').then((m) => ({
    default: m.AdminShell,
  })),
)
const AdminDashboardPage = lazy(
  () => import('@/features/admin/dashboard/dashboard-page'),
)
const AdminArticleCategoriesPage = lazy(
  () =>
    import('@/features/admin/article-categories/article-categories-page'),
)
const AdminPromptCategoriesPage = lazy(
  () => import('@/features/admin/prompt-categories/prompt-categories-page'),
)
const AdminCouponsPage = lazy(
  () => import('@/features/admin/coupons/coupons-page'),
)
const AdminAnnouncementsPage = lazy(
  () => import('@/features/admin/announcements/announcements-page'),
)
const AdminContactMessagesPage = lazy(
  () => import('@/features/admin/contact-messages/contact-messages-page'),
)
const AdminPromptEngineeringPage = lazy(
  () => import('@/features/admin/prompt-engineering/prompt-engineering-page'),
)
const AdminErrorsPage = lazy(
  () => import('@/features/admin/errors/errors-page'),
)
const AdminAnalyticsPage = lazy(
  () => import('@/features/admin/analytics/analytics-page'),
)
const AdminCommentsModerationPage = lazy(
  () => import('@/features/admin/comments/comments-moderation-page'),
)
const AdminAutoPublishPage = lazy(
  () => import('@/features/admin/auto-publish/auto-publish-page'),
)
const AdminMediaLibraryPage = lazy(
  () => import('@/features/admin/media/media-library-page'),
)
const AdminSubscribersPage = lazy(
  () => import('@/features/admin/subscribers/subscribers-page'),
)
const AdminAiNewsPage = lazy(
  () => import('@/features/admin/ai-news/ai-news-page'),
)
const AdminSocialPublisherPage = lazy(
  () => import('@/features/admin/social-publisher/social-publisher-page'),
)
const AdminNavbarPage = lazy(
  () => import('@/features/admin/navbar/navbar-page'),
)
// AI Course
const AdminAiCourseHub = lazy(
  () => import('@/features/admin/ai-course/hub-page'),
)
const AdminAiCourseSettings = lazy(
  () => import('@/features/admin/ai-course/settings-page'),
)
const AdminAiCourseVideos = lazy(
  () => import('@/features/admin/ai-course/videos-page'),
)
const AdminAiCourseImages = lazy(
  () => import('@/features/admin/ai-course/images-page'),
)
const AdminAiCoursePlans = lazy(
  () => import('@/features/admin/ai-course/plans-page'),
)
const AdminAiCourseTestimonials = lazy(
  () => import('@/features/admin/ai-course/testimonials-page'),
)
const AdminAiCourseFaqs = lazy(
  () => import('@/features/admin/ai-course/faqs-page'),
)
const AdminPagesManager = lazy(
  () => import('@/features/admin/pages-manager/pages-manager-page'),
)
const AdminHomeSectionsPage = lazy(
  () => import('@/features/admin/home-sections/home-sections-page'),
)
const AdminCoursesPage = lazy(
  () => import('@/features/admin/courses/courses-page'),
)
const AdminCourseLessonsPage = lazy(
  () => import('@/features/admin/courses/course-lessons-page'),
)
const AdminCourseCategoriesPage = lazy(
  () => import('@/features/admin/courses/categories-page'),
)
const AdminCourseAnnouncementsPage = lazy(
  () => import('@/features/admin/courses/announcements-page'),
)
const AdminTutorialsPage = lazy(
  () => import('@/features/admin/tutorials/tutorials-page'),
)
const AdminTutorialVideosPage = lazy(
  () => import('@/features/admin/tutorials/tutorial-videos-page'),
)
const AdminPromptsPage = lazy(
  () => import('@/features/admin/prompts/prompts-page'),
)
const AdminAIToolsPage = lazy(
  () => import('@/features/admin/ai-tools/ai-tools-page'),
)
const AdminFreeToolsPage = lazy(
  () => import('@/features/admin/free-tools/free-tools-page'),
)
const AdminReviewsPage = lazy(
  () => import('@/features/admin/reviews/reviews-page'),
)
const AdminZatoonaPage = lazy(
  () => import('@/features/admin/zatoona/zatoona-page'),
)
const AdminSiteSettingsPage = lazy(
  () => import('@/features/admin/site-settings/site-settings-page'),
)
const AdminQuotasPage = lazy(() => import('@/features/admin/quotas/quotas-page'))
const AdminPaymentsPage = lazy(
  () => import('@/features/admin/payments/payments-page'),
)
const AdminStudentsPage = lazy(
  () => import('@/features/admin/students/students-page'),
)
// AI Studio
const AdminStudioArticles = lazy(
  () => import('@/features/admin/studio/article-generator-page'),
)
const AdminStudioPrompts = lazy(
  () => import('@/features/admin/studio/prompt-generator-page'),
)
const AdminStudioAITools = lazy(
  () => import('@/features/admin/studio/ai-tools-generator-page'),
)
const AdminStudioZatoona = lazy(
  () => import('@/features/admin/studio/zatoona-generator-page'),
)
const AdminStudioCameraAngles = lazy(
  () => import('@/features/admin/studio/camera-angles-page'),
)
const AdminArticlesPage = lazy(
  () => import('@/features/admin/articles/articles-page'),
)
const AdminArticleEditorPage = lazy(
  () => import('@/features/admin/articles/article-editor-page'),
)
const AdminArticleNewPage = lazy(
  () => import('@/features/admin/articles/article-new-page'),
)
const StudioPage = lazy(() => import('@/features/studio/studio-page'))

const withFallback = (
  Component: React.ComponentType,
  routeId: string,
) => (
  <RouteErrorBoundary routeId={routeId}>
    <Suspense fallback={<RouteFallback />}>
      <Component />
    </Suspense>
  </RouteErrorBoundary>
)

/**
 * Wrap a route element in the page-status gate so the catalog shells
 * (vibe-coding, prompts, toolshub) honour status overrides too. The
 * PublicLayout already wraps its children, so this is for routes that
 * don't share that layout.
 */
const gated = (element: React.ReactNode) => (
  <PageStatusGate>{element}</PageStatusGate>
)

const router = createBrowserRouter([
  {
    path: '/',
    element: <PublicLayout />,
    children: [
      { index: true, element: <HomePage /> },

      { path: 'courses', element: withFallback(CoursesPage, 'courses') },
      {
        path: 'course/:slug',
        element: withFallback(CourseDetailPage, 'course-detail'),
      },

      { path: 'tutorials', element: withFallback(TutorialsPage, 'tutorials') },
      {
        path: 'tutorials/playlist/:slug',
        element: withFallback(TutorialsPlaylistPage, 'tutorials-playlist'),
      },

      // Pro tools (internal, احترافية) — المبنية جوا الموقع
      { path: 'tools', element: withFallback(ProToolsPage, 'pro-tools') },
      // Free tools (external AI Studio links, مجانية) — 14 tool خارجي
      { path: 'freetools', element: withFallback(FreeToolsPage, 'freetools') },
      { path: 'freetools/ai-search', element: withFallback(AiSearchPage, 'ai-search') },
      { path: 'freetools/ai-search/c/:threadId', element: withFallback(AiSearchPage, 'ai-search-thread') },
      { path: 'freetools/ai-search/history', element: withFallback(AiSearchPage, 'ai-search-history') },
      {
        path: 'freetools/:slug',
        element: withFallback(FreeToolDetailPage, 'freetool-detail'),
      },
      // Legacy /tools/:slug internal tool detail lives also under /tools/:slug
      {
        path: 'tools/:slug',
        element: withFallback(FreeToolDetailPage, 'pro-tool-detail'),
      },

      { path: 'zatoona', element: withFallback(ZatoonaHubPage, 'zatoona') },
      {
        path: 'zatoona/:slug',
        element: withFallback(ZatoonaCategoryPage, 'zatoona-category'),
      },
      {
        path: 'zatoona/:categorySlug/:slug',
        element: withFallback(ZatoonaReferencePage, 'zatoona-reference'),
      },

      {
        path: 'prompt-guide',
        element: withFallback(PromptGuidePage, 'prompt-guide'),
      },

      { path: 'free-apis/:slug', element: withFallback(FreeApiDetailPage, 'free-api-detail') },

      { path: 'blog/:slug', element: withFallback(ArticlePage, 'article') },

      {
        path: 'library',
        element: withFallback(PublicLibraryPage, 'library'),
      },

      {
        path: 'glossary',
        element: withFallback(GlossaryPage, 'glossary'),
      },

      { path: 'ai-news', element: withFallback(AiNewsPage, 'ai-news') },
      {
        path: 'ai-news/:slug',
        element: withFallback(AiNewsDetailPage, 'ai-news-detail'),
      },

      {
        path: 'about',
        element: withFallback(AboutPage, 'about'),
      },
      { path: 'contact', element: withFallback(ContactPage, 'contact') },
      { path: 'privacy', element: withFallback(PrivacyPage, 'privacy') },
      { path: 'terms', element: withFallback(TermsPage, 'terms') },
      {
        path: 'coming-soon',
        element: withFallback(ComingSoonPage, 'coming-soon'),
      },
      {
        path: 'maintenance',
        element: withFallback(MaintenancePage, 'maintenance'),
      },

      {
        path: 'vibe-coding/:kind/:slug',
        element: withFallback(VibeDetailPage, 'vibe-detail'),
      },
      {
        path: 'ai-course',
        element: withFallback(AiCourseLandingPage, 'ai-course'),
      },
      { path: 'checkout', element: withFallback(CheckoutPage, 'checkout') },
      {
        path: 'payment-success',
        element: withFallback(PaymentSuccessPage, 'payment-success'),
      },
      {
        path: 'verify/:certificateNumber',
        element: withFallback(VerifyCertificatePage, 'verify-certificate'),
      },
      { path: 'assistant', element: withFallback(AssistantPage, 'assistant') },

      { path: 'ai-tools', element: withFallback(AiToolsPillarPage, 'pillar-ai-tools') },
      { path: 'free-ai-tools', element: withFallback(FreeAiToolsPillarPage, 'pillar-free-ai-tools') },
      { path: 'ai-prompts', element: withFallback(AiPromptsPillarPage, 'pillar-ai-prompts') },
      { path: 'ai-design-course', element: withFallback(AiDesignCoursePillarPage, 'pillar-ai-design') },
      { path: 'ai-video-course', element: withFallback(AiVideoCoursePillarPage, 'pillar-ai-video') },

      { path: 'studio', element: withFallback(StudioPage, 'studio') },
      { path: 'studio/:projectId', element: withFallback(StudioPage, 'studio-project') },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
  // Auth routes — no public layout (focused split-screen surface)
  { path: '/login', element: withFallback(LoginPage, 'login') },
  { path: '/register', element: withFallback(RegisterPage, 'register') },
  {
    path: '/forgot-password',
    element: withFallback(ForgotPasswordPage, 'forgot-password'),
  },

  // LLM Leaderboard removed; blog has its own catalog shell (sidebar + top bar)
  { path: '/blog', element: gated(withFallback(BlogPage, 'blog')) },
  // Vibe Coding catalog — its own shell (sidebar + top bar)
  { path: '/vibe-coding', element: gated(withFallback(VibeCodingPage, 'vibe-coding')) },
  // Design Systems catalog — same shell pattern as vibe-coding
  { path: '/design-systems', element: gated(withFallback(DesignSystemsPage, 'design-systems')) },
  { path: '/design-systems/how-to-use', element: gated(withFallback(HowToUseDesignMdPage, 'design-systems-how-to')) },
  { path: '/design-systems/:slug', element: gated(withFallback(DesignSystemDetailPage, 'design-system-detail')) },
  // Prompts catalog — same shell layout
  { path: '/prompts', element: gated(withFallback(PromptsShellPage, 'prompts')) },
  { path: '/prompts/:id', element: gated(withFallback(PromptDetailPage, 'prompt-detail')) },
  // Tools Hub catalog — same shell layout
  { path: '/toolshub', element: gated(withFallback(ToolsHubShellPage, 'toolshub')) },
  { path: '/toolshub/:slug', element: gated(withFallback(ToolDetailPage, 'tool-detail')) },

  // Free APIs Directory — same catalog shell
  { path: '/free-apis', element: gated(withFallback(FreeApisHubPage, 'free-apis')) },

  // Prompt Engineering Guide — bilingual reference, same shell layout
  {
    path: '/prompt-engineering',
    element: gated(withFallback(PeHubPage, 'pe-hub')),
  },
  {
    path: '/prompt-engineering/*',
    element: gated(withFallback(PePage, 'pe-page')),
  },

  // Authenticated student area
  {
    path: '/student',
    element: <ProtectedRoute />,
    children: [
      {
        path: 'dashboard',
        element: withFallback(StudentDashboardPage, 'student-dashboard'),
      },
      {
        path: 'settings',
        element: withFallback(StudentSettingsPage, 'student-settings'),
      },
      {
        path: 'library',
        element: withFallback(LibraryPage, 'student-library'),
      },
      {
        path: 'certificates',
        element: withFallback(StudentCertificatesPage, 'student-certificates'),
      },
    ],
  },
  // Authenticated learning view (separate root because it has its own layout)
  {
    path: '/course/:slug/learn',
    element: <ProtectedRoute />,
    children: [
      { index: true, element: withFallback(CourseLearnPage, 'course-learn') },
    ],
  },

  // Admin panel — gated by role check, lazy-loaded
  {
    path: '/iamnotsite',
    element: <ProtectedRoute roles={['admin']} />,
    children: [
      {
        element: (
          <RouteErrorBoundary routeId="admin-shell">
            <Suspense fallback={<RouteFallback />}>
              <AdminShell />
            </Suspense>
          </RouteErrorBoundary>
        ),
        children: [
          {
            index: true,
            element: withFallback(AdminDashboardPage, 'admin-dashboard'),
          },
          {
            path: 'articles/categories',
            element: withFallback(
              AdminArticleCategoriesPage,
              'admin-article-categories',
            ),
          },
          {
            path: 'prompts/categories',
            element: withFallback(
              AdminPromptCategoriesPage,
              'admin-prompt-categories',
            ),
          },
          {
            path: 'coupons',
            element: withFallback(AdminCouponsPage, 'admin-coupons'),
          },
          {
            path: 'announcements',
            element: withFallback(
              AdminAnnouncementsPage,
              'admin-announcements',
            ),
          },
          {
            path: 'contact-messages',
            element: withFallback(
              AdminContactMessagesPage,
              'admin-contact-messages',
            ),
          },
          {
            path: 'navbar',
            element: withFallback(AdminNavbarPage, 'admin-navbar'),
          },
          // AI Course module
          {
            path: 'ai-course',
            element: withFallback(AdminAiCourseHub, 'admin-ai-course-hub'),
          },
          {
            path: 'ai-course/settings',
            element: withFallback(
              AdminAiCourseSettings,
              'admin-ai-course-settings',
            ),
          },
          {
            path: 'ai-course/videos',
            element: withFallback(AdminAiCourseVideos, 'admin-ai-course-videos'),
          },
          {
            path: 'ai-course/images',
            element: withFallback(AdminAiCourseImages, 'admin-ai-course-images'),
          },
          {
            path: 'ai-course/plans',
            element: withFallback(AdminAiCoursePlans, 'admin-ai-course-plans'),
          },
          {
            path: 'ai-course/testimonials',
            element: withFallback(
              AdminAiCourseTestimonials,
              'admin-ai-course-testimonials',
            ),
          },
          {
            path: 'ai-course/faqs',
            element: withFallback(AdminAiCourseFaqs, 'admin-ai-course-faqs'),
          },
          {
            path: 'pages',
            element: withFallback(AdminPagesManager, 'admin-pages-manager'),
          },
          {
            path: 'home-sections',
            element: withFallback(
              AdminHomeSectionsPage,
              'admin-home-sections',
            ),
          },
          {
            path: 'courses',
            element: withFallback(AdminCoursesPage, 'admin-courses'),
          },
          {
            path: 'courses/:courseId/lessons',
            element: withFallback(
              AdminCourseLessonsPage,
              'admin-course-lessons',
            ),
          },
          {
            path: 'courses/categories',
            element: withFallback(
              AdminCourseCategoriesPage,
              'admin-course-categories',
            ),
          },
          {
            path: 'courses/:courseId/announcements',
            element: withFallback(
              AdminCourseAnnouncementsPage,
              'admin-course-announcements',
            ),
          },
          {
            path: 'tutorials',
            element: withFallback(AdminTutorialsPage, 'admin-tutorials'),
          },
          {
            path: 'prompt-engineering',
            element: withFallback(
              AdminPromptEngineeringPage,
              'admin-prompt-engineering',
            ),
          },
          {
            path: 'errors',
            element: withFallback(AdminErrorsPage, 'admin-errors'),
          },
          {
            path: 'analytics',
            element: withFallback(AdminAnalyticsPage, 'admin-analytics'),
          },
          {
            path: 'comments',
            element: withFallback(
              AdminCommentsModerationPage,
              'admin-comments',
            ),
          },
          {
            path: 'auto-publish',
            element: withFallback(
              AdminAutoPublishPage,
              'admin-auto-publish',
            ),
          },
          {
            path: 'media',
            element: withFallback(AdminMediaLibraryPage, 'admin-media'),
          },
          {
            path: 'subscribers',
            element: withFallback(AdminSubscribersPage, 'admin-subscribers'),
          },
          {
            path: 'ai-news',
            element: withFallback(AdminAiNewsPage, 'admin-ai-news'),
          },
          {
            path: 'social-publisher',
            element: withFallback(AdminSocialPublisherPage, 'admin-social-publisher'),
          },
          {
            path: 'tutorials/:playlistId/videos',
            element: withFallback(
              AdminTutorialVideosPage,
              'admin-tutorial-videos',
            ),
          },
          {
            path: 'prompts',
            element: withFallback(AdminPromptsPage, 'admin-prompts'),
          },
          {
            path: 'ai-tools',
            element: withFallback(AdminAIToolsPage, 'admin-ai-tools'),
          },
          {
            path: 'free-tools',
            element: withFallback(AdminFreeToolsPage, 'admin-free-tools'),
          },
          {
            path: 'reviews',
            element: withFallback(AdminReviewsPage, 'admin-reviews'),
          },
          {
            path: 'zatoona',
            element: withFallback(AdminZatoonaPage, 'admin-zatoona'),
          },
          {
            path: 'site-settings',
            element: withFallback(
              AdminSiteSettingsPage,
              'admin-site-settings',
            ),
          },
          {
            path: 'quotas',
            element: withFallback(AdminQuotasPage, 'admin-quotas'),
          },
          {
            path: 'payments',
            element: withFallback(AdminPaymentsPage, 'admin-payments'),
          },
          {
            path: 'students',
            element: withFallback(AdminStudentsPage, 'admin-students'),
          },
          // AI Studio
          {
            path: 'studio/articles',
            element: withFallback(
              AdminStudioArticles,
              'admin-studio-articles',
            ),
          },
          {
            path: 'studio/prompts',
            element: withFallback(AdminStudioPrompts, 'admin-studio-prompts'),
          },
          {
            path: 'studio/ai-tools',
            element: withFallback(
              AdminStudioAITools,
              'admin-studio-ai-tools',
            ),
          },
          {
            path: 'studio/zatoona',
            element: withFallback(
              AdminStudioZatoona,
              'admin-studio-zatoona',
            ),
          },
          {
            path: 'studio/camera-angles',
            element: withFallback(
              AdminStudioCameraAngles,
              'admin-studio-camera-angles',
            ),
          },
          {
            path: 'articles',
            element: withFallback(AdminArticlesPage, 'admin-articles'),
          },
          {
            path: 'articles/new',
            element: withFallback(AdminArticleNewPage, 'admin-article-new'),
          },
          {
            path: 'articles/:id/edit',
            element: withFallback(
              AdminArticleEditorPage,
              'admin-article-editor',
            ),
          },
          // Catch-all for unknown admin paths — keeps the admin shell
          // (sidebar + topbar) but renders the bilingual not-found page
          // inside instead of breaking the layout.
          { path: '*', element: <NotFoundPage /> },
        ],
      },
    ],
  },
])

export function AppRouter() {
  return <RouterProvider router={router} />
}
